import { computed } from 'vue';
import { SelectedTemplateAsBlueprint, TemplateFormPayload, usePlanningCreateDutyStore } from '../store/usePlanningCreateDutyStore';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useDutyStore, useUserStore } from '@/rennovation/planning-view/store';
import { useDutyTemplateBridgeStore } from '@/rennovation/planning-view/store/bridge/dutyTemplateBridge';
import { useSetupBridgeStore } from '@/rennovation/planning-view/store/bridge/setupBridge';
import { RecentDutyItem } from '@/rennovation/planning-view/types/api/duties-recent-response';
import { DutyTemplateItem, TemplateQualification } from '@/rennovation/planning-view/types/api/duty-template-response';
import { ApiDuty, DutyBreak } from '@/rennovation/planning-view/types/api/duties-response';
import { ApiSetupResponse } from '@/rennovation/planning-view/types/api/setup-response';
import { formatSecondsToTimeString } from '@/rennovation/shared/utils/time-utils';
import { useI18n } from 'vue-i18n';

import { useUserGroupBridge } from '../store/useUserGroupBridge';
import { Mode } from '../store/usePlanningCreateDutyStore';

export interface CurrentDayOption {
	id: string;
	name: string;
	raw: ApiDuty;
	isCurrentDayTrack: boolean;
}

export function useDutyTemplatesLoader() {
	const { t } = useI18n();
	const createDutyStore = usePlanningCreateDutyStore();
	const planningBridge = usePlanningBridgeStore();
	const dutyStore = useDutyStore();
	const userStore = useUserStore();
	const userGroupBridge = useUserGroupBridge();
	const dutyTemplateBridge = useDutyTemplateBridgeStore();
	const setupBridge = useSetupBridgeStore();

	const recentDutiesOptions = computed<SelectedTemplateAsBlueprint[]>(() => {
		return (planningBridge.recentDuties || []).map((item: RecentDutyItem) => {
			const rawDate = item.dayObject?.date || item.date;

			const formattedDate = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';
			const baseName = item.name || t('unnamed_shift');

			return {
				id: item._id || item.id,
				name: formattedDate ? `${baseName} (${formattedDate})` : baseName,
				raw: item,
				isRecentTrack: true,
			};
		});
	});
	const currentDayOptions = computed<CurrentDayOption[]>(() => {
		const dateStr = createDutyStore.selectedDate;
		if (!dateStr) return [];
		return createDutyStore.mode === Mode.Planning ? dutyStore.currentDayDuties.data.all : [];
	});

	const driverCategorizedOptions = computed(() => {
		if (createDutyStore.mode !== Mode.Planning) {
			return [];
		}
		return currentDayOptions.value
			.map((option) => {
				const duty = option;
				const driver = duty.user ? userGroupBridge.usersMappedByID[duty?.user] : null;
				if (driver?.lastName) {
					const driverName = `${driver.lastName}, ${driver.firstName}`;
					return {
						id: option.id,
						name: `${driverName} (${duty.name})`,
						driverName,
						raw: duty,
					};
				}
				return {
					id: option.id,
					name: option.name,
					driverName: `unknown driver`,
					raw: duty,
				};
			})
			.sort((a, b) => {
				if (a.driverName === undefined && b.driverName !== undefined) return 1;
				if (a.driverName !== undefined && b.driverName === undefined) return -1;
				if (a.driverName === undefined && b.driverName === undefined) return a.name.localeCompare(b.name);
				return a.driverName.localeCompare(b.driverName);
			});
	});

	const systemTemplatesOptions = computed(() => {
		const rawTemplates = dutyTemplateBridge.dutyTemplates ?? [];

		return rawTemplates
			.filter((template): template is DutyTemplateItem => !template?.provider)
			.map((template) => ({
				id: template.id || template._id,
				name: template.name || t('unnamed_template'),
				raw: template,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	});

	const buildFormPayloadFromTemplate = (target: SelectedTemplateAsBlueprint['raw']): TemplateFormPayload | null => {
		if (!target) return null;

		const incomingQuantities = target.extraPayAccountQuantity || {};
		const hasBasicDataField = 'hasBasicData' in target ? (target as DutyTemplateItem).hasBasicData : true;

		const basicDataFields =
			hasBasicDataField === true
				? {
						startStop: target.startStop || '',
						endStop: target.endStop || '',
						start: typeof target.start === 'number' ? formatSecondsToTimeString(target.start) : null,
						end: typeof target.end === 'number' ? formatSecondsToTimeString(target.end) : null,
						standbyStart: typeof target.standbyStart === 'number' ? formatSecondsToTimeString(target.standbyStart) : null,
						standbyEnd: typeof target.standbyEnd === 'number' ? formatSecondsToTimeString(target.standbyEnd) : null,
						breaks: Array.isArray(target.breaks)
							? target.breaks.map((b: DutyBreak) => ({
									paid: !!b.paid,
									start: formatSecondsToTimeString(b.start),
									end: formatSecondsToTimeString(b.end),
									duration: formatSecondsToTimeString(
										b.duration !== undefined ? b.duration : b.end >= b.start ? b.end - b.start : 86400 - b.start + b.end
									),
								}))
							: [],
					}
				: {};

		return {
			name: target.name || '',
			root: target.root || '',
			provider: target.provider,
			providerId: target.providerId,
			calculatedWorkTime: formatSecondsToTimeString(target.workTime || 0),
			extraPayAccountQuantity: incomingQuantities,
			qualifications:
				'qualifications' in target && Array.isArray(target.qualifications)
					? (target.qualifications as TemplateQualification[]).map((q) => (typeof q === 'object' ? q._id : q))
					: [],
			notes: 'notes' in target ? target.notes || '' : '',
			labels: 'labels' in target && Array.isArray(target.labels) ? target.labels : [],
			additionalWorkTime: formatSecondsToTimeString(target.additionalWorkTime || 0),
			...basicDataFields,
		};
	};

	const applySelectedTemplateToForm = async (selectedItem: SelectedTemplateAsBlueprint | null | undefined): Promise<void> => {
		if (!selectedItem) return;

		const selectDutyFrom = createDutyStore.form.selectDutyFrom;

		const selectedData = selectedItem.raw ?? selectedItem;

		const id = selectedItem?.id ?? selectedItem?._id ?? selectedItem?.raw?.id ?? selectedItem?.raw?._id;

		if (!id) return;

		const isPartialSelection = selectDutyFrom === 'Current day' || selectDutyFrom === 'Driver';

		const template = isPartialSelection ? await dutyStore.fetchDutyByID(id) : selectedData;

		if (!template) return;

		const payload = buildFormPayloadFromTemplate(template);

		if (payload) {
			createDutyStore.applyTemplateToForm(payload);
		}
	};

	const preloadModalData = async (): Promise<void> => {
		const requests: Promise<RecentDutyItem[] | DutyTemplateItem[] | ApiSetupResponse | void>[] = [
			planningBridge.fetchRecentDuties(),
			setupBridge.loadSetup(),
		];

		if (createDutyStore.mode === Mode.Planning) requests.push(dutyTemplateBridge.fetchDutyTemplates(false, false));

		if (!userStore.usersById || Object.keys(userStore.usersById).length === 0) {
			requests.push(userStore.fetchUsers());
		}

		try {
			await Promise.all(requests);
		} catch (error) {
			console.error('Failed to preload modal template data layers:', error);
		}
	};

	return {
		recentDutiesOptions,
		currentDayOptions,
		driverCategorizedOptions,
		systemTemplatesOptions,
		applySelectedTemplateToForm,
		preloadModalData,
	};
}
